/**
 * Centralized error handling utilities
 */

// Log errors to console with additional context
export function logError(error: unknown, context?: Record<string, any>): void {
  if (error instanceof Error) {
    console.error(`Error: ${error.message}`, {
      stack: error.stack,
      ...context
    });
  } else {
    console.error('Unknown error:', error, context);
  }
  
  // Here you could also send errors to a monitoring service like Sentry
  // if (process.env.NODE_ENV === 'production') {
  //   Sentry.captureException(error, { extra: context });
  // }
}

// Format error messages for display to users
export function formatErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    // Clean up common error messages to be more user-friendly
    const message = error.message;
    
    if (message.includes('network') || message.includes('fetch')) {
      return 'Network error. Please check your internet connection and try again.';
    }
    
    if (message.includes('timeout')) {
      return 'The request timed out. Please try again.';
    }
    
    if (message.includes('auth/') || message.includes('authentication')) {
      return 'Authentication error. Please sign in again.';
    }
    
    if (message.includes('permission') || message.includes('access')) {
      return 'You don\'t have permission to perform this action.';
    }
    
    // Return the original message if no specific handling
    return message;
  }
  
  // Default message for unknown errors
  return 'An unexpected error occurred. Please try again.';
}

// Parse Supabase errors
export function parseSupabaseError(error: any): string {
  if (!error) return 'Unknown error';
  
  // Handle PostgreSQL error codes
  if (error.code) {
    switch (error.code) {
      case '23505': // unique_violation
        return 'This record already exists.';
      case '23503': // foreign_key_violation
        return 'This operation references a record that doesn\'t exist.';
      case '42P01': // undefined_table
        return 'System error: Database table not found.';
      case '42703': // undefined_column
        return 'System error: Database column not found.';
      case '22P02': // invalid_text_representation
        return 'Invalid input format.';
      default:
        break;
    }
  }
  
  // Handle auth errors
  if (error.message?.includes('Email not confirmed')) {
    return 'Please check your email and confirm your account before signing in.';
  }
  
  if (error.message?.includes('Invalid login credentials')) {
    return 'Incorrect email or password. Please try again.';
  }
  
  // Return the original message or a default one
  return error.message || 'An error occurred with the database operation.';
}

// Retry a failed operation
export async function retryOperation<T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> {
  let lastError: Error;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      
      if (attempt === maxRetries) {
        throw lastError;
      }
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay * attempt));
    }
  }
  
  throw new Error('Max retries exceeded');
}